import { createSelector, OutputSelector } from 'reselect';
import { arrsToSelectableObject } from '../../core/utils/methods';
import { PermittingDetailsSelectorValues } from '../../models/interfaces';
import { AppState } from '../reducers';
import { PermittingState } from '../reducers/permitting.reducer';

const permittingState = (state: AppState): PermittingState => state.permitting;

type PermittingDetailsDataKeys =
  | 'project'
  | 'scoping'
  | 'site'
  | 'leasing'
  | 'categories'
  | 'permittingUsers';

export namespace PermittingSelectors {
  export const getJurisdictionList = createSelector(
    [permittingState],
    (state: PermittingState) => state.jurisdictionList,
  );

  export const getJurisdictionListLikeSelectorOptions = createSelector(
    [permittingState],
    (state: PermittingState) =>
      arrsToSelectableObject(state.jurisdictionList, 'id', 'jurisdictionName'),
  );

  export const getPermittingBrowseData = createSelector(
    [permittingState],
    (state: PermittingState) => state.browseData,
  );

  export const getPermittingDetailsData = (
    keys: Array<PermittingDetailsDataKeys>,
  ): OutputSelector<
    [typeof permittingState],
    Partial<PermittingDetailsSelectorValues>,
    (res: PermittingState) => Partial<PermittingDetailsSelectorValues>
  > =>
    createSelector([permittingState], (state: PermittingState) => {
      const {
        categories,
        leasing,
        permittingUsers,
        site,
        scoping,
        ...project
      } = state.detailsData;

      const obj: PermittingDetailsSelectorValues = {
        project,
        categories,
        leasing,
        permittingUsers,
        scoping,
        site,
      };

      return keys.reduce(
        (
          acc: Partial<PermittingDetailsSelectorValues>,
          v: PermittingDetailsDataKeys,
        ) => ({ ...acc, [v]: obj[v] }),
        {} as Partial<PermittingDetailsSelectorValues>,
      );
    });

  export const getPermittingDetailsHeaderData = createSelector(
    [permittingState],
    (state: PermittingState) => state.detailsData.site,
  );

  export const getProjectType = createSelector(
    [permittingState],
    (state: PermittingState) => state.tabData.summary.project.projectType.value,
  );

  export const getProjectId = createSelector(
    [permittingState],
    (state: PermittingState) => state.tabData.summary.project.id,
  );

  export const getProjectIdSafe = createSelector(
    [permittingState],
    (state: PermittingState) => state?.tabData?.summary?.project?.id,
  );

  export const getPermittingUsers = createSelector(
    [permittingState],
    (state: PermittingState) =>
      state?.tabData?.jurisdictionAudit?.permittingUsers,
  );

  export const getPermitTypeTableData = (permittingId?: string) =>
    createSelector(
      [permittingState],
      ({
        originalPermitTypeTableData,
        currentPermitTypeTableData,
      }: PermittingState) => ({
        originalPermitTypeTableData: permittingId
          ? originalPermitTypeTableData[permittingId]
          : [],
        currentPermitTypeTableData: permittingId
          ? currentPermitTypeTableData[permittingId]
          : [],
      }),
    );

  export const getAllPermitTypeTablesData = createSelector(
    [permittingState],
    ({
      originalPermitTypeTableData,
      currentPermitTypeTableData,
    }: PermittingState) => ({
      originalPermitTypeTableData,
      currentPermitTypeTableData,
    }),
  );

  // Tab data
  export const getTabLoadingState = (tabName: string) =>
    createSelector(
      [permittingState],
      state => state.loadingTabs[tabName] || false,
    );

  export const getTabLoadedState = (tabName: string) =>
    createSelector(
      [permittingState],
      state => state.loadedTabs[tabName] || false,
    );

  export const getSummaryTabData = createSelector(
    [permittingState],
    state => state.tabData.summary,
  );

  export const getSiteData = createSelector(
    [permittingState],
    state => state.tabData.summary.site,
  );

  export const getWorkflowTabData = createSelector(
    [permittingState],
    state => state.tabData.workflow,
  );

  export const getContactsTabData = createSelector(
    [permittingState],
    state => state.tabData.contacts,
  );

  export const getJurisdictionAuditTabData = createSelector(
    [permittingState],
    state => state.tabData.jurisdictionAudit,
  );

  export const getSummaryProjectType = createSelector(
    [permittingState],
    state => state.tabData.summary.project?.projectType.value,
  );
}
